import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import { formatDate } from '../utils/dateFormat';
import { getBrandImageUrl } from '../utils/browserBranding';
import InventoryDevicePicker from '../components/InventoryDevicePicker';

const writeRoles = ['superadmin', 'admin', 'whitelabel_admin', 'employee'];
const paymentOptions = [
  { value: 'cash', label: 'Cash' },
  { value: 'online', label: 'Online' },
  { value: 'upi', label: 'UPI' },
  { value: 'gpay', label: 'GPay' },
  { value: 'paytm', label: 'Paytm' },
  { value: 'phonepe', label: 'PhonePe' },
  { value: 'credit', label: 'Credit' },
];

const emptyCustomerForm = {
  name: '',
  mobile: '',
  driver_name: '',
  address: '',
  vehicle_no: '',
  device_type: '',
  device_model: '',
  imei: '',
  sim_no: '',
  installation_date: '',
  renewal_date: '',
  validity_months: 12,
  gps_tracking_enabled: false,
  last_data_time: '',
  gps_online_status: 'offline'
};

const emptyAddon = {
  service_type_id: '',
  expiry_date: '',
  amount: '',
  paid_amount: '',
  notes: ''
};

const vehicleServiceSelectionKeys = [
  'gps_device_renewal',
  'insurance',
  'fitness_certificate',
  'pollution_puc',
  'rto_tax',
  'number_plate',
  'permit_service',
  'national_permit',
  'state_permit',
  'road_tax',
  'fastag',
  'vehicle_service'
];

const vehicleServiceShortLabels = {
  gps_device_renewal: 'GPS Renewal',
  insurance: 'Insurance',
  fitness_certificate: 'Fitness',
  pollution_puc: 'PUC',
  rto_tax: 'RTO Tax',
  number_plate: 'Number Plate',
  permit_service: 'Permit',
  national_permit: 'National Permit',
  state_permit: 'State Permit',
  road_tax: 'Road Tax',
  fastag: 'FASTag',
  vehicle_service: 'Other Service'
};

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch {
    return {};
  }
};

const money = (value) => `Rs. ${Number(value || 0).toFixed(2)}`;
const normalizeDigits = (value) => String(value || '').replace(/\D/g, '');
const normalizeVehicleNo = (value) => String(value || '').replace(/\s+/g, '').toLowerCase();
const sameMobile = (left, right) => {
  const a = normalizeDigits(left);
  const b = normalizeDigits(right);
  return a.length >= 8 && b.length >= 8 && a.slice(-10) === b.slice(-10);
};
const sameVehicle = (left, right) => {
  const a = normalizeVehicleNo(left);
  const b = normalizeVehicleNo(right);
  return Boolean(a && b && a === b);
};
const findExactCustomerMatch = (customer, rows) => {
  const uniqueRows = Array.from(new Map((rows || []).map((row) => [Number(row.id), row])).values());
  const vehicleMatch = uniqueRows.find((row) => sameVehicle(row.vehicle_no, customer.vehicle_no));
  if (normalizeVehicleNo(customer.vehicle_no)) return vehicleMatch || null;
  return uniqueRows.find((row) => sameMobile(row.mobile, customer.mobile)) || null;
};
const quickServiceLabel = (type) => vehicleServiceShortLabels[type?.service_key] || type?.name || 'Service';
const safeText = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;'
}[char]));
const toDateInputValue = (date) => {
  if (!date || Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const calculateRenewalDate = (installationDate, validityMonths) => {
  const date = installationDate ? new Date(installationDate) : new Date();
  if (Number.isNaN(date.getTime())) return '';
  date.setMonth(date.getMonth() + Number(validityMonths || 0));
  return toDateInputValue(date);
};

const getSalesViewConfig = (view) => ({
  'device-sale': { baseView: 'device-sale', viewType: 'dashboard', saleMode: 'device_services', customerMode: 'new', title: 'Device Sales', createTitle: 'Create Device Sale', addButtonLabel: '+ New Customer' },
  'sales-new-customer': { baseView: 'device-sale', viewType: 'new_customer_form', saleMode: 'device_services', customerMode: 'new', title: 'New Customer', createTitle: 'Create New Customer Device Sale' },
  'sales-existing-customer': { baseView: 'device-sale', viewType: 'existing_customer_form', saleMode: 'device_services', customerMode: 'existing', title: 'Existing Customer', createTitle: 'Create Existing Customer Device Sale' },
  'service-sale': { baseView: 'service-sale', viewType: 'service_form', saleMode: 'services_only', customerMode: 'new', title: 'New Service Sales', createTitle: 'Create New Service Sale' },
  'sales-service-existing': { baseView: 'service-sale', viewType: 'existing_service_form', saleMode: 'services_only', customerMode: 'existing', title: 'Existing Customer Sales', createTitle: 'Create Existing Customer Service Sale' }
}[view] || null);

export default function Sales({ embedded = false, salesView = '' }) {
  const user = getStoredUser();
  const salesViewConfig = useMemo(() => getSalesViewConfig(salesView), [salesView]);
  const activeSalesView = salesViewConfig?.baseView || '';
  const activeSalesViewType = salesViewConfig?.viewType || 'dashboard';
  const isSalesDashboardView = activeSalesViewType === 'dashboard';
  const isLockedSalesFormView = !isSalesDashboardView;
  const isExistingCustomerView = activeSalesViewType.includes('existing');
  const canAddSale = writeRoles.includes(user?.role);
  const canManageSales = ['superadmin', 'admin', 'whitelabel_admin'].includes(user?.role);
  const canUseInventoryStock = ['superadmin', 'admin', 'whitelabel_admin', 'employee'].includes(user?.role);
  const [sales, setSales] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [stock, setStock] = useState([]);
  const [salesUsers, setSalesUsers] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [customerMode, setCustomerMode] = useState('new');
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerMatches, setCustomerMatches] = useState([]);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [customerHistory, setCustomerHistory] = useState(null);
  const [autoServices, setAutoServices] = useState({ gps_renewal: false, warranty: false, installation: false });
  const [serviceAddons, setServiceAddons] = useState([]);
  const [saleMode, setSaleMode] = useState(salesViewConfig?.saleMode || 'device_only');
  const [productId, setProductId] = useState('');
  const [inventoryId, setInventoryId] = useState('');
  const [salePrice, setSalePrice] = useState('');
  const [installCharge, setInstallCharge] = useState(0);
  const [paidAmount, setPaidAmount] = useState('');
  const [paymentMode, setPaymentMode] = useState('cash');
  const [saleType, setSaleType] = useState('direct');
  const [salesUserId, setSalesUserId] = useState('');
  const [notes, setNotes] = useState('');
  const [customerForm, setCustomerForm] = useState(emptyCustomerForm);
  const [invoiceData, setInvoiceData] = useState(null);
  const [invoiceLoadingId, setInvoiceLoadingId] = useState(null);
  const [invoiceDraft, setInvoiceDraft] = useState(null);
  const [invoiceViewMode, setInvoiceViewMode] = useState('original');
  const [editingSale, setEditingSale] = useState(null);
  const [editForm, setEditForm] = useState({});
  const invoiceFrameRef = useRef(null);

  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);
  const applySaleMode = useCallback((mode) => {
    setSaleMode(mode);
    if (mode === 'services_only') {
      setProductId('');
      setInventoryId('');
      setSalePrice('');
      setInstallCharge(0);
      setPaidAmount('');
      setAutoServices({ gps_renewal: false, warranty: false, installation: false });
    } else {
      setAutoServices({ gps_renewal: false, warranty: false, installation: false });
    }
  }, []);

  useEffect(() => {
    if (!salesViewConfig) return;
    applySaleMode(salesViewConfig.saleMode);
    setCustomerMode(salesViewConfig.customerMode || 'new');
    setSelectedCustomerId('');
    setCustomerSearch('');
    setCustomerMatches([]);
    setCustomerHistory(null);
    if (canAddSale) setShowForm(salesViewConfig.viewType !== 'dashboard');
  }, [applySaleMode, canAddSale, salesViewConfig]);

  const fetchAll = useCallback(async () => {
    try {
      const [salesRes, custRes] = await Promise.all([
        axios.get(`${API_URL}/api/sales`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/customers`, { headers: getHeaders() }),
      ]);

      setSales(salesRes.data);
      setCustomers(custRes.data);

      if (canAddSale) {
        const [prodRes, stockRes, usersRes, serviceRes] = await Promise.allSettled([
          axios.get(`${API_URL}/api/sales/products`, { headers: getHeaders() }),
          canUseInventoryStock
            ? axios.get(`${API_URL}/api/inventory/stock?status=available`, { headers: getHeaders() })
            : Promise.resolve({ data: [] }),
          axios.get(`${API_URL}/api/sales/users`, { headers: getHeaders() }),
          axios.get(`${API_URL}/api/services/types`, { headers: getHeaders() }),
        ]);
        if (prodRes.status === 'fulfilled') setProducts(prodRes.value.data || []);
        if (stockRes.status === 'fulfilled') setStock(stockRes.value.data || []);
        if (usersRes.status === 'fulfilled') setSalesUsers(usersRes.value.data || []);
        if (serviceRes.status === 'fulfilled') setServiceTypes(serviceRes.value.data || []);
      }
    } catch (err) {
      console.log(err);
    }
  }, [canAddSale, canUseInventoryStock, getHeaders]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const selectedCustomer = useMemo(() => (
    customers.find((item) => Number(item.id) === Number(selectedCustomerId)) || null
  ), [customers, selectedCustomerId]);
  const exactExistingCustomerMatch = useMemo(() => {
    if (customerMode !== 'new') return null;
    return findExactCustomerMatch(customerForm, [...customerMatches, ...customers]);
  }, [customerForm, customerMatches, customerMode, customers]);
  const quickServiceAddons = useMemo(() => (
    vehicleServiceSelectionKeys
      .map((key) => serviceTypes.find((item) => item.service_key === key))
      .filter(Boolean)
  ), [serviceTypes]);

  useEffect(() => {
    if (!showForm || !canAddSale) return undefined;
    const term = [
      customerSearch,
      customerForm.mobile,
      customerForm.vehicle_no,
      customerForm.imei
    ].find((value) => String(value || '').trim().length >= 3);

    if (!term) {
      setCustomerMatches([]);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setLookupBusy(true);
      try {
        const res = await axios.get(`${API_URL}/api/sales/customers/lookup`, {
          headers: getHeaders(),
          params: {
            q: customerSearch || undefined,
            mobile: customerForm.mobile || undefined,
            vehicle_no: customerForm.vehicle_no || undefined,
            imei: customerForm.imei || undefined
          }
        });
        if (!cancelled) setCustomerMatches(res.data || []);
      } catch (err) {
        if (!cancelled) setCustomerMatches([]);
      } finally {
        if (!cancelled) setLookupBusy(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [canAddSale, customerForm.imei, customerForm.mobile, customerForm.vehicle_no, customerSearch, getHeaders, showForm]);

  useEffect(() => {
    if (!selectedCustomerId) {
      setCustomerHistory(null);
      return undefined;
    }

    let cancelled = false;
    axios.get(`${API_URL}/api/sales/customers/${selectedCustomerId}/history`, { headers: getHeaders() })
      .then((res) => { if (!cancelled) setCustomerHistory(res.data); })
      .catch(() => { if (!cancelled) setCustomerHistory(null); });

    return () => {
      cancelled = true;
    };
  }, [getHeaders, selectedCustomerId]);

  const hasDeviceSale = saleMode !== 'services_only';
  const hasServiceSale = saleMode !== 'device_only';
  const saleTotal = hasDeviceSale ? (Number(salePrice || 0) || 0) + (Number(installCharge || 0) || 0) : 0;
  const salePaid = Number(paidAmount || 0) || 0;
  const saleBalance = Math.max(saleTotal - salePaid, 0);
  const calculatedPayStatus = salePaid <= 0 ? 'pending' : saleBalance <= 0 ? 'paid' : 'partial';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canAddSale) return;
    const selectedServiceAddons = serviceAddons
      .filter((item) => item.service_type_id && (saleMode === 'services_only' || item.expiry_date || item.amount || item.paid_amount || item.notes))
      .map((item) => ({
        ...item,
        status: item.expiry_date ? 'upcoming' : 'converted',
        service_source: saleMode === 'services_only' ? 'only_reminder' : 'renewal_lead'
      }));

    if (hasDeviceSale && selectedServiceAddons.length > 0) {
      const hasZeroAmountServices = selectedServiceAddons.some(s => Number(s.amount || 0) <= 0);
      if (hasZeroAmountServices) {
        alert('Please enter an amount for all added services during a device sale to create a valid ledger entry.');
        return;
      }
    }

    const resolvedCustomerId = customerMode === 'existing'
      ? selectedCustomerId
      : (exactExistingCustomerMatch?.id ? String(exactExistingCustomerMatch.id) : '');
    if (customerMode === 'existing' && !resolvedCustomerId) {
      alert('Select an existing customer or use New Customer mode');
      return;
    }
    if (hasDeviceSale && !productId) {
      alert('Select a product for the device sale');
      return;
    }
    if (hasDeviceSale && !Number(salePrice || 0)) {
      alert('Enter the sale price for the device sale');
      return;
    }
    if (hasDeviceSale && canUseInventoryStock && productId && availableStock.length > 0 && !inventoryId) {
      alert('Search and select the exact inventory device before saving this sale');
      return;
    }
    if (saleMode === 'services_only' && selectedServiceAddons.length === 0) {
      alert('Select at least one service for the Only Services sale');
      return;
    }
    if (hasDeviceSale && salePaid > saleTotal) {
      alert('Paid amount cannot be greater than the sale total');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/sales/customer-sale`, {
        customer: {
          ...customerForm,
          payment_status: calculatedPayStatus
        },
        sale: {
          sale_mode: saleMode,
          customer_id: resolvedCustomerId || null,
          product_id: hasDeviceSale ? productId : null,
          inventory_id: hasDeviceSale ? (inventoryId || null) : null,
          sale_price: hasDeviceSale ? salePrice : 0,
          installation_charge: hasDeviceSale ? installCharge : 0,
          paid_amount: hasDeviceSale ? paidAmount : 0,
          payment_mode: paymentMode,
          sale_type: saleType,
          sales_user_id: salesUserId || null,
          payment_status: hasDeviceSale ? calculatedPayStatus : 'pending',
          notes,
          auto_services: hasDeviceSale ? autoServices : { gps_renewal: false, warranty: false, installation: false },
          service_addons: hasServiceSale ? selectedServiceAddons : []
        }
      }, { headers: getHeaders() });
      const login = res.data?.customer_login;
      const servicesText = Array.isArray(res.data?.services_created) && res.data.services_created.length > 0
        ? `\nServices created: ${res.data.services_created.length}`
        : '';
      const invoiceText = res.data?.invoice_no ? `\nInvoice No: ${res.data.invoice_no}` : '';
      const loginText = login
        ? `\nCustomer login: ${login.username || customerForm.mobile}${login.initial_password ? `\nTemporary password: ${login.initial_password}` : '\nPassword: existing customer password'}`
        : '';
      alert(`${res.data?.message || 'Sale saved successfully!'}${invoiceText}${loginText}${servicesText}`);
      setShowForm(false);
      setCustomerMode('new');
      setSelectedCustomerId('');
      setCustomerSearch('');
      setCustomerMatches([]);
      setCustomerHistory(null);
      setServiceAddons([]);
      const resetMode = salesViewConfig?.saleMode || (activeSalesView === 'service-sale' ? 'services_only' : 'device_only');
      setSaleMode(resetMode);
      setAutoServices({ gps_renewal: false, warranty: false, installation: false });
      setProductId('');
      setInventoryId('');
      setSalePrice('');
      setInstallCharge(0);
      setPaidAmount('');
      setPaymentMode('cash');
      setSalesUserId('');
      setNotes('');
      setCustomerForm(emptyCustomerForm);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const inp = { width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };
  const lbl = { display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 'bold', color: '#374151' };
  const allAvailableStock = stock.filter((item) => item.status === 'available');
  const availableStock = allAvailableStock.filter((item) => !productId || String(item.product_id) === String(productId));
  const updateCustomer = (key, value) => setCustomerForm((current) => {
    const next = { ...current, [key]: value };
    if (key === 'installation_date' || key === 'validity_months') {
      next.renewal_date = calculateRenewalDate(
        key === 'installation_date' ? value : next.installation_date,
        key === 'validity_months' ? value : next.validity_months
      );
    }
    if (key === 'gps_tracking_enabled' && !value) {
      next.gps_online_status = 'offline';
      next.last_data_time = '';
    }
    return next;
  });
  const selectExistingCustomer = (customer) => {
    setCustomerMode('existing');
    setSelectedCustomerId(String(customer.id));
    setCustomerSearch(`${customer.name || ''} ${customer.mobile || ''}`.trim());
    setCustomerForm((current) => ({
      ...current,
      name: customer.name || '',
      mobile: customer.mobile || '',
      driver_name: customer.driver_name || '',
      address: customer.address || '',
      vehicle_no: customer.vehicle_no || '',
      device_type: customer.device_type || current.device_type || '',
      device_model: customer.device_model || current.device_model || '',
      imei: customer.imei || current.imei || '',
      sim_no: customer.sim_no || current.sim_no || '',
      installation_date: customer.installation_date ? String(customer.installation_date).slice(0, 10) : current.installation_date,
      renewal_date: customer.renewal_date ? String(customer.renewal_date).slice(0, 10) : current.renewal_date,
      validity_months: customer.validity_months || current.validity_months || 12,
      gps_tracking_enabled: Boolean(customer.gps_tracking_enabled),
      gps_online_status: customer.gps_online_status || 'offline'
    }));
  };

  const startNewCustomer = (keepOwner = false) => {
    setCustomerMode('new');
    setSelectedCustomerId('');
    setCustomerHistory(null);
    if (!keepOwner) return;
    setCustomerForm((current) => ({
      ...emptyCustomerForm,
      name: current.name,
      mobile: current.mobile,
      address: current.address,
      validity_months: current.validity_months || 12
    }));
  };

  const addServiceAddon = (serviceTypeId = '') => {
    setServiceAddons((current) => ([
      ...current,
      {
        ...emptyAddon,
        service_type_id: serviceTypeId || serviceTypes.find((item) => item.service_key !== 'gps_device_renewal')?.id || serviceTypes[0]?.id || ''
      }
    ]));
  };

  const updateServiceAddon = (index, key, value) => {
    setServiceAddons((current) => current.map((item, itemIndex) => (
      itemIndex === index ? { ...item, [key]: value } : item
    )));
  };

  const removeServiceAddon = (index) => {
    setServiceAddons((current) => current.filter((_, itemIndex) => itemIndex !== index));
  };

  const mergeVisibleDuplicates = async () => {
    if (!selectedCustomerId) return alert('Select a target customer first');
    const sourceIds = customerMatches
      .filter((item) => Number(item.id) !== Number(selectedCustomerId))
      .filter((item) => item.mobile && selectedCustomer?.mobile && String(item.mobile) === String(selectedCustomer.mobile))
      .map((item) => item.id);
    if (sourceIds.length === 0) return alert('No duplicate customers with the same mobile number were found in this list');
    if (!window.confirm(`Merge ${sourceIds.length} duplicate customer record(s) into the selected customer?`)) return;
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/sales/customers/merge`, {
        target_customer_id: selectedCustomerId,
        source_customer_ids: sourceIds
      }, { headers: getHeaders() });
      alert('Duplicate customers merged successfully');
      setCustomerMatches([]);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'Merge failed');
    } finally {
      setLoading(false);
    }
  };

  const applyInventorySelection = (id) => {
    setInventoryId(id);
    const item = stock.find((row) => String(row.id) === String(id));
    if (!item) return;
    if (item.product_id) setProductId(String(item.product_id));
    setCustomerForm((current) => ({
      ...current,
      imei: item.imei || current.imei || '',
      sim_no: item.sim_no || item.sim_no_2 || item.serial_no || current.sim_no || '',
      device_type: item.device_type || item.product_type || item.type || current.device_type || 'GPS Tracking Device',
      device_model: item.device_name || item.name || item.model || current.device_model || ''
    }));
  };
  const openInvoice = async (sale) => {
    setInvoiceLoadingId(sale.id);
    try {
      const res = await axios.get(`${API_URL}/api/sales/${sale.id}/invoice`, { headers: getHeaders() });
      const nextInvoice = res.data;
      const savedDraft = loadInvoiceDraft(nextInvoice);
      setInvoiceData(nextInvoice);
      setInvoiceDraft(savedDraft);
      setInvoiceViewMode(savedDraft ? 'edited' : 'original');
    } catch (err) {
      alert(err.response?.data?.message || 'Could not generate the invoice');
    } finally {
      setInvoiceLoadingId(null);
    }
  };

  const closeInvoice = () => {
    setInvoiceData(null);
    setInvoiceDraft(null);
    setInvoiceViewMode('original');
  };

  const openEditSale = (sale) => {
    setEditingSale(sale);
    setEditForm({
      customer_name: sale.customer_name || '',
      customer_mobile: sale.customer_mobile || '',
      driver_name: sale.driver_name || '',
      address: sale.address || '',
      vehicle_no: sale.vehicle_no || '',
      device_type: sale.product_type || '',
      device_model: sale.device_model || sale.product_name || '',
      product_id: sale.product_id || '',
      imei: sale.imei || sale.customer_imei || '',
      sim_no: sale.sim_no || sale.serial_no || '',
      quantity: sale.quantity || 1,
      sale_price: sale.sale_price || 0,
      installation_charge: sale.installation_charge || 0,
      paid_amount: sale.paid_amount || 0,
      payment_mode: 'cash',
      sale_type: sale.sale_type || 'direct',
      sales_user_id: sale.tagged_user_id || sale.sold_by || '',
      dealer_id: sale.dealer_id || '',
      gps_tracking_enabled: Number(sale.gps_tracking_enabled || 0) === 1,
      notes: sale.notes || ''
    });
  };

  const updateEditField = (field, value) => {
    setEditForm((current) => ({ ...current, [field]: value }));
  };

  const saveEditedSale = async (event) => {
    event.preventDefault();
    if (!editingSale) return;
    const total = (Number(editForm.sale_price || 0) * Number(editForm.quantity || 1)) + Number(editForm.installation_charge || 0);
    if (Number(editForm.paid_amount || 0) > total) {
      alert('Paid amount cannot be greater than sale total');
      return;
    }
    setLoading(true);
    try {
      await axios.patch(`${API_URL}/api/sales/${editingSale.id}`, editForm, { headers: getHeaders() });
      alert('Sale updated successfully');
      setEditingSale(null);
      setEditForm({});
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'Sale update failed');
    } finally {
      setLoading(false);
    }
  };

  const deleteSaleRecord = async (sale) => {
    if (!window.confirm(`Delete sale ${sale.invoice_no || `#${sale.id}`}? This will remove linked auto payment/service/tracking records for this sale.`)) return;
    setLoading(true);
    try {
      await axios.delete(`${API_URL}/api/sales/${sale.id}`, { headers: getHeaders() });
      alert('Sale deleted successfully');
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'Sale delete failed');
    } finally {
      setLoading(false);
    }
  };

  const getInvoiceItems = (invoice) => {
    if (!invoice) return [];
    const sale = invoice.sale || {};
    const device = invoice.device || {};
    const customer = invoice.customer || {};
    const quantity = Number(sale.quantity || 1);
    const salePrice = Number(sale.sale_price || 0);
    const installCharge = Number(sale.installation_charge || 0);
    const invoiceDate = invoice.invoice_date ? formatDate(invoice.invoice_date) : '';
    const productCode = device.model_number || device.product_model || device.device_oem_name || device.name || device.product_name || device.product_type || '-';
    const vehicle = customer.vehicle_no || customer.name || '-';
    const rows = [{
      date: invoiceDate,
      productCode,
      passenger: vehicle,
      title: device.name || device.product_name || 'Device',
      detail: [
        device.device_type,
        device.imei ? `IMEI ${device.imei}` : '',
        device.sim_no ? `SIM ${device.sim_no}` : '',
        device.serial_no ? `Serial ${device.serial_no}` : ''
      ].filter(Boolean).join(' | '),
      quantity,
      rate: salePrice,
      amount: salePrice * quantity
    }];
    if (installCharge > 0) {
      rows.push({
        date: invoiceDate,
        productCode: 'INSTALL',
        passenger: vehicle,
        title: 'Installation Charge',
        detail: customer.vehicle_no || '',
        quantity: 1,
        rate: installCharge,
        amount: installCharge
      });
    }
    return rows;
  };

  const buildInvoiceHtml = (invoice) => {
    const seller = invoice?.seller || {};
    const customer = invoice?.customer || {};
    const sale = invoice?.sale || {};
    const logo = getBrandImageUrl(seller.logo_url);
    const items = getInvoiceItems(invoice);
    const editableAttrs = (field, label) => `contenteditable="true" data-invoice-field="${safeText(field)}" data-invoice-label="${safeText(label)}"`;
    const blankRowCount = Math.max(12 - items.length, 8);
    const rows = [
      ...items.map((item, index) => `
        <tr>
          <td class="editable" ${editableAttrs(`item-${index + 1}-date`, `Row ${index + 1} Date`)}>${safeText(item.date || '-')}</td>
          <td class="editable" ${editableAttrs(`item-${index + 1}-product-code`, `Row ${index + 1} Product Code`)}>${safeText(item.productCode || '-')}</td>
          <td class="editable" ${editableAttrs(`item-${index + 1}-vehicle`, `Row ${index + 1} Vehicle`)}>${safeText(item.passenger || '-')}</td>
          <td class="editable" ${editableAttrs(`item-${index + 1}-description`, `Row ${index + 1} Description`)}><strong>${safeText(item.title)}</strong>${item.detail ? `<br><span>${safeText(item.detail)}</span>` : ''}</td>
          <td class="amount editable" ${editableAttrs(`item-${index + 1}-fare`, `Row ${index + 1} Fare`)}>${safeText(money(item.amount))}</td>
        </tr>
      `),
      ...Array.from({ length: blankRowCount }).map(() => '<tr class="blank-row"><td></td><td></td><td></td><td></td><td></td></tr>')
    ].join('');
    const contactLine = [seller.mobile, seller.email].filter(Boolean).join(' | ');
    const sellerAddress = seller.address || 'Address';
    const subtotal = Number(sale.total_amount || 0);
    const paid = Number(sale.paid_amount || 0);
    const balance = Number(sale.balance_amount || 0);
    const notes = [
      sale.notes,
      sale.payment_status ? `Payment Status: ${sale.payment_status}` : '',
      customer.renewal_date ? `Renewal: ${formatDate(customer.renewal_date)}` : ''
    ].filter(Boolean).join(' | ');

    return `<!doctype html>
      <html>
      <head>
        <title>${safeText(invoice?.invoice_no || 'Invoice')}</title>
        <style>
          *{box-sizing:border-box}
          body{font-family:Georgia,'Times New Roman',serif;color:#111;margin:0;background:#f3f4f6}
          .page{width:860px;min-height:1110px;margin:20px auto;background:#fff;padding:62px 48px 42px;box-shadow:0 18px 45px rgba(15,23,42,.12)}
          .top{display:grid;grid-template-columns:90px 1fr 190px;gap:18px;align-items:start}
          .logo-box{width:86px;height:68px;display:flex;align-items:center;justify-content:center}
          .logo-box img{max-width:86px;max-height:68px;object-fit:contain}
          .company h2{font-size:18px;letter-spacing:.5px;color:#174f5a;margin:0 0 6px;font-weight:900}
          .company p{font-family:Arial,sans-serif;margin:3px 0;font-size:13px;line-height:1.25}
          .invoice-title{text-align:right;color:#174f5a;font-size:34px;font-weight:900;letter-spacing:1px;margin-top:-6px}
          .meta{display:grid;grid-template-columns:1fr 110px 185px;margin-top:2px;align-items:center;font-family:Arial,sans-serif;font-size:13px}
          .meta .label{text-align:right;font-weight:900;padding-right:8px}
          .rule{height:8px;border-top:3px solid #9edbe7;border-bottom:1px solid #9edbe7;margin:10px 0 12px}
          .bill-grid{display:grid;grid-template-columns:1fr 290px;gap:20px;min-height:135px}
          .label-box{display:inline-block;min-width:102px;text-align:center;background:#dceff3;border:1px solid #174f5a;font-weight:900;font-size:13px;padding:4px 10px}
          .bill-text{font-family:Arial,sans-serif;margin-top:8px;font-size:13px;line-height:1.45}
          .mini-table{width:100%;border-collapse:collapse;margin-top:0;font-size:13px}
          .mini-table th{background:#dceff3;border:1px solid #174f5a;padding:4px 8px;text-align:center;color:#111}
          .mini-table td{border:1px solid #174f5a;padding:5px 8px;text-align:center;height:22px;font-family:Arial,sans-serif}
          .items{width:100%;border-collapse:collapse;margin-top:18px;table-layout:fixed}
          .items th{background:#dceff3;border:1px solid #174f5a;padding:5px 7px;text-align:center;font-size:13px;color:#111}
          .items td{border-left:1px solid #333;border-right:1px solid #333;padding:6px 8px;font-family:Arial,sans-serif;font-size:12px;height:28px;vertical-align:top}
          .items tbody tr:nth-child(even) td{background:#f1f1f1}
          .items tbody tr:nth-child(odd) td{background:#fff}
          .items tbody tr:first-child td{border-top:1px solid #333}
          .items tbody tr:last-child td{border-bottom:1px solid #333}
          .items .amount{text-align:right}
          .items span{color:#555}
          .summary-row{display:grid;grid-template-columns:1fr 286px;gap:20px}
          .notes-title{font-weight:900;margin:3px 0 4px;font-size:13px}
          .notes-box{border:1px dotted #555;height:112px;padding:10px;font-family:Arial,sans-serif;font-size:12px;line-height:1.35}
          .totals{margin-left:auto;width:286px}
          .total-line{display:grid;grid-template-columns:1fr 118px;align-items:center;margin-bottom:8px;font-size:14px}
          .total-line span{text-align:right;padding-right:10px}
          .total-line strong{border:1px solid #333;height:28px;display:flex;align-items:center;justify-content:flex-end;padding:0 9px;font-family:Arial,sans-serif;font-size:13px}
          .footer{margin-top:50px;background:#f2f2f2;text-align:center;padding:12px 10px;font-size:13px;font-style:italic}
          .no-print{text-align:center;margin-top:18px}.no-print button{font-family:Arial,sans-serif;padding:10px 18px;background:#174f5a;color:white;border:0;border-radius:4px;cursor:pointer}
          .editable{min-height:16px;outline:1px dashed transparent}
          .editable:hover,.editable:focus{outline-color:#9edbe7;background:#f8fdff}
          @media print{body{background:white}.page{margin:0;width:auto;min-height:auto;box-shadow:none;padding:44px 38px 32px}.no-print{display:none}}
        </style>
      </head>
      <body>
        <div class="page">
          <div class="top">
            <div class="logo-box"><img src="${safeText(logo)}" alt=""></div>
            <div class="company">
              <h2 class="editable" ${editableAttrs('seller-name', 'Seller Name')}>${safeText(seller.name || 'BizTrack CRM')}</h2>
              <p class="editable" ${editableAttrs('seller-address', 'Seller Address')}>${safeText(sellerAddress)}</p>
              <p class="editable" ${editableAttrs('seller-contact', 'Seller Contact')}>${safeText(contactLine || 'Contact Info.')}</p>
            </div>
            <div class="invoice-title">INVOICE</div>
          </div>
          <div class="meta">
            <div></div>
            <div class="label">DATE:</div>
            <div class="editable" ${editableAttrs('invoice-date', 'Invoice Date')}>${safeText(invoice?.invoice_date ? formatDate(invoice.invoice_date) : '-')}</div>
            <div></div>
            <div class="label">INVOICE #:</div>
            <div class="editable" ${editableAttrs('invoice-no', 'Invoice Number')}>${safeText(invoice?.invoice_no || '-')}</div>
          </div>
          <div class="rule"></div>
          <div class="bill-grid">
            <div>
              <span class="label-box">Bill To:</span>
              <div class="bill-text editable" ${editableAttrs('bill-to', 'Bill To')}>
                <strong>${safeText(customer.name || '-')}</strong><br>
                ${safeText(customer.mobile || '-')}<br>
                Address: ${safeText(customer.address || '-')}<br>
                ${customer.vehicle_no ? `Vehicle: ${safeText(customer.vehicle_no)}` : ''}
              </div>
            </div>
            <table class="mini-table">
              <thead><tr><th>Terms</th><th>Due Date</th></tr></thead>
              <tbody>
                <tr><td class="editable" ${editableAttrs('terms', 'Terms')}>${safeText(sale.payment_status || '-')}</td><td class="editable" ${editableAttrs('due-date', 'Due Date')}>${safeText(customer.renewal_date ? formatDate(customer.renewal_date) : '-')}</td></tr>
              </tbody>
            </table>
          </div>
          <table class="items">
            <colgroup><col style="width:95px"><col style="width:102px"><col style="width:92px"><col><col style="width:120px"></colgroup>
            <thead><tr><th>Date</th><th>Product code</th><th>Vehicle</th><th>Description</th><th>Fare</th></tr></thead>
            <tbody>${rows}</tbody>
          </table>
          <div class="summary-row">
            <div>
              <div class="notes-title">NOTES:</div>
              <div class="notes-box editable" ${editableAttrs('notes', 'Notes')}>${safeText(notes || '')}</div>
            </div>
            <div class="totals">
              <div class="total-line"><span>SUBTOTAL</span><strong class="editable" ${editableAttrs('subtotal', 'Subtotal')}>${safeText(money(subtotal))}</strong></div>
              <div class="total-line"><span>PAID</span><strong class="editable" ${editableAttrs('paid', 'Paid')}>${safeText(money(paid))}</strong></div>
              <div class="total-line"><span>TOTAL DUE</span><strong class="editable" ${editableAttrs('total-due', 'Total Due')}>${safeText(money(balance))}</strong></div>
            </div>
          </div>
          <div class="footer">THANK YOU FOR YOUR BUSINESS!</div>
          <div class="no-print"><button onclick="window.print()">Print / Save PDF</button></div>
        </div>
      </body>
      </html>`;
  };

  const invoiceDraftKey = (invoice) => {
    const id = invoice?.sale_id || invoice?.invoice_no;
    return id ? `biztrack.invoiceDraft.${id}` : '';
  };

  const normalizeInvoiceFieldValue = (value) => String(value || '').replace(/\s+/g, ' ').trim();

  const loadInvoiceDraft = (invoice) => {
    try {
      const key = invoiceDraftKey(invoice);
      if (!key) return null;
      const draft = JSON.parse(localStorage.getItem(key) || 'null');
      return draft?.html ? draft : null;
    } catch {
      return null;
    }
  };

  const hasSavedInvoiceDraft = (sale) => {
    try {
      const key = invoiceDraftKey({ sale_id: sale?.id || sale?.sale_id, invoice_no: sale?.invoice_no });
      return Boolean(key && localStorage.getItem(key));
    } catch {
      return false;
    }
  };

  const getInvoiceFrameDocument = () => invoiceFrameRef.current?.contentDocument || invoiceFrameRef.current?.contentWindow?.document || null;

  const getInvoiceFieldValues = (doc) => {
    if (!doc) return {};
    return Array.from(doc.querySelectorAll('[data-invoice-field]')).reduce((acc, element) => {
      const field = element.getAttribute('data-invoice-field');
      if (!field) return acc;
      acc[field] = {
        label: element.getAttribute('data-invoice-label') || field,
        value: normalizeInvoiceFieldValue(element.innerText)
      };
      return acc;
    }, {});
  };

  const getOriginalInvoiceFieldValues = (invoice) => {
    if (typeof DOMParser === 'undefined') return {};
    const doc = new DOMParser().parseFromString(buildInvoiceHtml(invoice), 'text/html');
    return getInvoiceFieldValues(doc);
  };

  const getInvoiceChanges = (invoice, doc) => {
    const original = getOriginalInvoiceFieldValues(invoice);
    const current = getInvoiceFieldValues(doc);
    return Object.keys(current)
      .map((field) => ({
        field,
        label: current[field].label || original[field]?.label || field,
        original: original[field]?.value || '',
        edited: current[field].value || ''
      }))
      .filter((item) => item.original !== item.edited);
  };

  const saveEditedInvoiceDraft = ({ silent = false } = {}) => {
    if (!invoiceData) return null;
    const doc = getInvoiceFrameDocument();
    const key = invoiceDraftKey(invoiceData);
    if (!doc || !key) return null;
    const changes = getInvoiceChanges(invoiceData, doc);

    if (changes.length === 0) {
      if (invoiceViewMode === 'edited') {
        localStorage.removeItem(key);
        setInvoiceDraft(null);
        setInvoiceViewMode('original');
      }
      if (!silent) alert('No edited invoice changes found. The original invoice will be used.');
      return null;
    }

    const draft = {
      sale_id: invoiceData.sale_id,
      invoice_no: invoiceData.invoice_no,
      updated_at: new Date().toISOString(),
      html: doc.documentElement.outerHTML,
      changes
    };
    localStorage.setItem(key, JSON.stringify(draft));
    setInvoiceDraft(draft);
    setInvoiceViewMode('edited');
    if (!silent) alert('Edited invoice draft saved. The original sale database was not updated.');
    return draft;
  };

  const resetEditedInvoiceDraft = () => {
    if (!invoiceData) return;
    if (!window.confirm('Delete the edited invoice draft and show the original invoice?')) return;
    const key = invoiceDraftKey(invoiceData);
    if (key) localStorage.removeItem(key);
    setInvoiceDraft(null);
    setInvoiceViewMode('original');
  };

  const printHtml = (html) => {
    const printWindow = window.open('', '_blank', 'width=980,height=720');
    if (!printWindow) return alert('Popup was blocked. Allow popups in your browser and try again.');
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const printCurrentFrame = () => {
    const frameWindow = invoiceFrameRef.current?.contentWindow;
    if (!frameWindow) return false;
    frameWindow.focus();
    frameWindow.print();
    return true;
  };

  const printEditedInvoice = () => {
    if (!invoiceData) return;
    const savedDraft = saveEditedInvoiceDraft({ silent: true });
    if (savedDraft && printCurrentFrame()) return;
    if (invoiceViewMode === 'edited') {
      if (printCurrentFrame()) return;
    }
    if (invoiceDraft?.html) return printHtml(invoiceDraft.html);
    printHtml(buildInvoiceHtml(invoiceData));
  };

  const printOriginalInvoice = () => {
    if (!invoiceData) return;
    printHtml(buildInvoiceHtml(invoiceData));
  };

  const salesViewLabel = salesViewConfig?.title || (activeSalesView === 'service-sale'
    ? 'Service Sale'
    : activeSalesView === 'device-sale'
      ? 'Device Sales'
      : 'Sales');
  const createSalesTitle = salesViewConfig?.createTitle || `Create ${salesViewLabel}`;
  const addButtonLabel = salesViewConfig?.addButtonLabel || (activeSalesView === 'service-sale'
    ? '+ Service Sale'
    : activeSalesView === 'device-sale'
      ? '+ Device Sale'
      : '+ Customer + Sale');
  const showExistingCustomerSearch = isExistingCustomerView;
  const showSaleFormDetails = !isExistingCustomerView || Boolean(selectedCustomerId);
  const showSalesList = !isLockedSalesFormView || !showForm;
  const editQuantity = Number(editForm.quantity || 1) || 1;
  const editSalePrice = Number(editForm.sale_price || 0) || 0;
  const editInstallCharge = Number(editForm.installation_charge || 0) || 0;
  const editPaidAmount = Number(editForm.paid_amount || 0) || 0;
  const editTotal = (editSalePrice * editQuantity) + editInstallCharge;
  const editBalance = Math.max(editTotal - editPaidAmount, 0);

  return (
    <div className={embedded ? undefined : 'fixed-panel-page'} style={embedded ? { padding: 0 } : undefined}>
      <div className={embedded ? undefined : 'fixed-panel-header'}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <div>
            <h2 style={{ color: '#1e3a5f', margin: 0 }}>{salesViewLabel}</h2>
            {!canAddSale && (
              <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: '13px' }}>
                View-only access for your own customer sales.
              </p>
            )}
          </div>
          {canAddSale && !isLockedSalesFormView && (
            <button onClick={() => setShowForm(!showForm)} style={{ background: '#1e3a5f', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>
              {showForm ? 'Cancel' : addButtonLabel}
            </button>
          )}
        </div>

      </div>

      <div className={embedded ? undefined : 'fixed-panel-body'}>
        {canAddSale && showForm && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <h3 style={{ color: '#1e3a5f', marginTop: 0 }}>{createSalesTitle}</h3>
            <form onSubmit={handleSubmit}>
              {selectedCustomerId && !isExistingCustomerView && (
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
                  <button type="button" onClick={() => startNewCustomer(true)} style={{ background: '#f8fafc', color: '#0369a1', border: '1px solid #bae6fd', borderRadius: '8px', padding: '9px 13px', cursor: 'pointer', fontWeight: 800, fontSize: '13px' }}>
                    Same owner new vehicle
                  </button>
                </div>
              )}

              {showExistingCustomerSearch && (
                <div style={{ background: '#f8fafc', border: '1px solid #dbeafe', borderRadius: '8px', padding: '14px', marginBottom: '16px' }}>
                  <label style={lbl}>Find Existing Customer</label>
                  <input
                    style={inp}
                    value={customerSearch}
                    onChange={(event) => setCustomerSearch(event.target.value)}
                    placeholder="Search by mobile, vehicle no, name, IMEI or SIM"
                  />
                  <small style={{ display: 'block', marginTop: '6px', color: '#64748b', fontSize: '12px' }}>
                    Enter a mobile number, vehicle number, or IMEI to show matching customers here.
                  </small>
                  {lookupBusy && <div style={{ marginTop: '10px', color: '#64748b', fontSize: '13px' }}>Searching existing customers...</div>}
                  {customerMatches.length > 0 && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '10px', marginTop: '12px' }}>
                      {customerMatches.map((item) => {
                        const active = Number(item.id) === Number(selectedCustomerId);
                        return (
                          <div key={item.id} style={{ border: `1px solid ${active ? '#10b981' : '#e5e7eb'}`, borderRadius: '8px', padding: '12px', background: active ? '#ecfdf5' : 'white' }}>
                            <strong style={{ display: 'block', color: '#111827' }}>{item.name || 'Customer'}</strong>
                            <small style={{ display: 'block', color: '#64748b', margin: '4px 0' }}>
                              {item.mobile || '-'} | {item.vehicle_no || 'No vehicle'} | {item.imei || 'No IMEI'}
                            </small>
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', margin: '8px 0' }}>
                              <span style={{ background: '#e0f2fe', color: '#0369a1', borderRadius: '999px', padding: '3px 8px', fontSize: '11px', fontWeight: 800 }}>{item.sales_count || 0} sales</span>
                              <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: '999px', padding: '3px 8px', fontSize: '11px', fontWeight: 800 }}>{money(item.pending_amount)} due</span>
                              <span style={{ background: '#ede9fe', color: '#6d28d9', borderRadius: '999px', padding: '3px 8px', fontSize: '11px', fontWeight: 800 }}>{item.same_owner_vehicles || 1} vehicle</span>
                            </div>
                            <button type="button" onClick={() => selectExistingCustomer(item)} style={{ background: active ? '#10b981' : '#1e3a5f', color: 'white', border: 'none', borderRadius: '7px', padding: '8px 10px', cursor: 'pointer', fontSize: '12px', fontWeight: 800 }}>
                              {active ? 'Selected' : 'Use Existing'}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {selectedCustomerId && ['superadmin', 'admin', 'whitelabel_admin'].includes(user?.role) && (
                    <button type="button" onClick={mergeVisibleDuplicates} style={{ marginTop: '12px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: '8px', padding: '9px 12px', cursor: 'pointer', fontWeight: 800, fontSize: '13px' }}>
                      Merge same-mobile duplicates into selected
                    </button>
                  )}
                </div>
              )}

              {!isExistingCustomerView && exactExistingCustomerMatch && (
                <div style={{ background: '#fff7ed', border: '1px solid #fdba74', borderRadius: '8px', padding: '14px', marginBottom: '16px', color: '#9a3412', fontSize: '13px' }}>
                  <strong style={{ display: 'block', marginBottom: '4px' }}>Existing customer matched</strong>
                  <span>
                    Mobile or vehicle number already belongs to {exactExistingCustomerMatch.name || 'this customer'} ({exactExistingCustomerMatch.mobile || '-'}{exactExistingCustomerMatch.vehicle_no ? ` | ${exactExistingCustomerMatch.vehicle_no}` : ''}). This sale will be saved under the existing customer instead of creating a duplicate.
                  </span>
                  <button type="button" onClick={() => selectExistingCustomer(exactExistingCustomerMatch)} style={{ marginTop: '10px', display: 'block', background: '#f97316', color: 'white', border: 'none', borderRadius: '7px', padding: '8px 11px', cursor: 'pointer', fontSize: '12px', fontWeight: 800 }}>
                    Open Existing Customer
                  </button>
                </div>
              )}

              {isExistingCustomerView && !selectedCustomerId && (
                <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px', padding: '14px', marginBottom: '16px', color: '#9a3412', fontSize: '13px', fontWeight: 800 }}>
                  Select an existing customer above to open this sale form.
                </div>
              )}

              {showSaleFormDetails && (
                <>
                  {customerHistory && (
                    <div style={{ background: 'white', border: '1px solid #d1fae5', borderRadius: '8px', padding: '14px', marginBottom: '16px' }}>
                      <h4 style={{ color: '#047857', margin: '0 0 10px' }}>Selected Customer History</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px' }}>
                        {[
                          { label: 'Sales', value: customerHistory.summary?.total_sales || 0 },
                          { label: 'Revenue', value: money(customerHistory.summary?.total_revenue) },
                          { label: 'Pending', value: money(customerHistory.summary?.pending_amount) },
                          { label: 'Open Services', value: customerHistory.summary?.open_services || 0 },
                          { label: 'Due Services', value: customerHistory.summary?.due_services || 0 },
                          { label: 'Owner Vehicles', value: customerHistory.summary?.same_owner_vehicles || 1 }
                        ].map((item) => (
                          <div key={item.label} style={{ background: '#f8fafc', borderRadius: '8px', padding: '10px' }}>
                            <small style={{ color: '#64748b' }}>{item.label}</small>
                            <strong style={{ display: 'block', color: '#111827', marginTop: '3px' }}>{item.value}</strong>
                          </div>
                        ))}
                      </div>
                      {customerHistory.services?.length > 0 && (
                        <div style={{ marginTop: '12px', display: 'grid', gap: '7px' }}>
                          {customerHistory.services.slice(0, 4).map((service) => (
                            <div key={service.id} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: '12px', color: '#374151', background: '#f9fafb', borderRadius: '7px', padding: '8px' }}>
                              <span>{service.service_name}</span>
                              <b>{service.expiry_date ? formatDate(service.expiry_date) : service.status}</b>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <h4 style={{ color: '#0b66ae', margin: '0 0 12px' }}>{hasDeviceSale ? 'Customer and Device Management' : 'Customer and Service Details'}</h4>
                  {canUseInventoryStock && hasDeviceSale && (
                    <div style={{ background: '#f8fafc', border: '1px solid #dbeafe', borderRadius: '8px', padding: '14px', marginBottom: '16px' }}>
                      <InventoryDevicePicker
                        items={allAvailableStock}
                        value={inventoryId}
                        onChange={(id) => applyInventorySelection(id)}
                        label={user?.role === 'employee' ? 'Admin Inventory Device' : 'Fetch Device Data From Inventory'}
                        placeholder="Search IMEI last 5, SIM, serial, device"
                        emptyMessage="No available admin inventory device found"
                        showSearchModes
                        inputStyle={inp}
                      />
                      <small style={{ display: 'block', marginTop: '6px', color: '#64748b', fontSize: '12px' }}>
                        {user?.role === 'employee'
                          ? `Showing available devices from your admin inventory only. Available devices: ${allAvailableStock.length}.`
                          : `Fetching from inventory removes the need for manual IMEI/SIM entry. Available devices: ${allAvailableStock.length}.`}
                      </small>
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '18px' }}>
                    <div><label style={lbl}>Customer Name *</label><input style={inp} value={customerForm.name} onChange={e => updateCustomer('name', e.target.value)} placeholder="Customer name" required /></div>
                    <div><label style={lbl}>Mobile *</label><input style={inp} value={customerForm.mobile} onChange={e => updateCustomer('mobile', e.target.value)} placeholder="Mobile number" required /></div>
                    <div><label style={lbl}>Driver Name</label><input style={inp} value={customerForm.driver_name} onChange={e => updateCustomer('driver_name', e.target.value)} placeholder="Driver name" /></div>
                    <div><label style={lbl}>Vehicle Number</label><input style={inp} value={customerForm.vehicle_no} onChange={e => updateCustomer('vehicle_no', e.target.value)} placeholder="Vehicle number" /></div>
                    {hasDeviceSale && (
                      <>
                        <div><label style={lbl}>IMEI</label><input style={inp} value={customerForm.imei} onChange={e => updateCustomer('imei', e.target.value)} placeholder="Device IMEI" /></div>
                        <div><label style={lbl}>SIM Number</label><input style={inp} value={customerForm.sim_no} onChange={e => updateCustomer('sim_no', e.target.value)} placeholder="SIM number" /></div>
                        <div><label style={lbl}>Device Type</label><input style={inp} value={customerForm.device_type} onChange={e => updateCustomer('device_type', e.target.value)} placeholder="GPS Tracking Device" /></div>
                        <div><label style={lbl}>Device Model</label><input style={inp} value={customerForm.device_model} onChange={e => updateCustomer('device_model', e.target.value)} placeholder="Device model" /></div>
                        <div>
                          <label style={lbl}>Enable Tracking For This Device</label>
                          <select style={inp} value={customerForm.gps_tracking_enabled ? 'yes' : 'no'} onChange={e => updateCustomer('gps_tracking_enabled', e.target.value === 'yes')}>
                            <option value="no">No - device details only</option>
                            <option value="yes">Yes - show GPS tracking to customer</option>
                          </select>
                        </div>
                      </>
                    )}
                    <div><label style={lbl}>Installation Date</label><input type="date" style={inp} value={customerForm.installation_date} onChange={e => updateCustomer('installation_date', e.target.value)} /></div>
                    <div><label style={lbl}>Renewal Date</label><input type="date" style={inp} value={customerForm.renewal_date} onChange={e => updateCustomer('renewal_date', e.target.value)} /></div>
                    <div><label style={lbl}>Validity Months</label><input type="number" style={inp} value={customerForm.validity_months} onChange={e => updateCustomer('validity_months', e.target.value)} /></div>
                    {hasDeviceSale && (
                      <>
                        <div><label style={lbl}>Online Status</label><select style={inp} value={customerForm.gps_online_status} onChange={e => updateCustomer('gps_online_status', e.target.value)} disabled={!customerForm.gps_tracking_enabled}><option value="offline">Offline</option><option value="online">Online</option></select></div>
                        <div><label style={lbl}>Last Data Time</label><input type="datetime-local" style={inp} value={customerForm.last_data_time} onChange={e => updateCustomer('last_data_time', e.target.value)} disabled={!customerForm.gps_tracking_enabled} /></div>
                      </>
                    )}
                    <div><label style={lbl}>Address</label><input style={inp} value={customerForm.address} onChange={e => updateCustomer('address', e.target.value)} placeholder="Customer address" /></div>
                    <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '12px', color: '#1d4ed8', fontSize: '13px', fontWeight: 800 }}>
                      {hasDeviceSale ? 'Customer login will be created automatically from this mobile number. GPS tracking appears only when this device is enabled for tracking.' : 'Only selected services will be saved for this customer and vehicle. Product and inventory device are not required.'}
                    </div>
                  </div>

                  {hasDeviceSale && (
                    <>
                      <h4 style={{ color: '#0b66ae', margin: '0 0 12px' }}>Device Sale Details</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
                        <div>
                          <label style={lbl}>Product *</label>
                          <select style={inp} value={productId} onChange={e => {
                            const nextProductId = e.target.value;
                            const product = products.find((item) => String(item.id) === String(nextProductId));
                            setProductId(nextProductId);
                            setInventoryId('');
                            setCustomerForm((current) => ({
                              ...current,
                              imei: inventoryId ? '' : current.imei,
                              sim_no: inventoryId ? '' : current.sim_no,
                              device_type: current.device_type || product?.type || 'GPS Tracking Device',
                              device_model: current.device_model || product?.model || ''
                            }));
                          }} required>
                            <option value="">Select product</option>
                            {products.map(p => <option key={p.id} value={p.id}>{p.name} ({p.type})</option>)}
                          </select>
                        </div>
                        <div><label style={lbl}>Sale Price *</label><input type="number" min="0" step="0.01" style={inp} value={salePrice} onChange={e => setSalePrice(e.target.value)} placeholder="Sale price" required /></div>
                        <div><label style={lbl}>Paid Amount</label><input type="number" min="0" max={saleTotal || undefined} step="0.01" style={inp} value={paidAmount} onChange={e => setPaidAmount(e.target.value)} placeholder="Customer paid amount" /></div>
                        <div><label style={lbl}>Installation Charge</label><input type="number" style={inp} value={installCharge} onChange={e => setInstallCharge(e.target.value)} placeholder="0" /></div>
                        <div>
                          <label style={lbl}>Payment Mode</label>
                          <select style={inp} value={paymentMode} onChange={e => setPaymentMode(e.target.value)} disabled={salePaid <= 0}>
                            {paymentOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                          </select>
                        </div>
                        <div style={{ background: '#f8fafc', border: '1px solid #dbeafe', borderRadius: '8px', padding: '12px' }}>
                          <label style={lbl}>Balance Amount</label>
                          <strong style={{ display: 'block', color: saleBalance > 0 ? '#dc2626' : '#10b981', fontSize: '20px' }}>{money(saleBalance)}</strong>
                          <small style={{ color: '#64748b' }}>Total {money(saleTotal)} | Paid {money(salePaid)}</small>
                        </div>
                        <div>
                          <label style={lbl}>Sale Type</label>
                          <select style={inp} value={saleType} onChange={e => setSaleType(e.target.value)}>
                            <option value="direct">Direct</option><option value="dealer">Dealer</option>
                            <option value="employee">Employee</option><option value="admin">Admin</option>
                          </select>
                        </div>
                        <div>
                          <label style={lbl}>Dealer / Sales User Tag</label>
                          <select style={inp} value={salesUserId} onChange={e => setSalesUserId(e.target.value)}>
                            <option value="">Current logged-in user</option>
                            {salesUsers.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                          </select>
                        </div>
                        <div>
                          <label style={lbl}>Payment Status</label>
                          <div style={{ ...inp, background: '#f8fafc', color: calculatedPayStatus === 'paid' ? '#065f46' : calculatedPayStatus === 'partial' ? '#92400e' : '#dc2626', fontWeight: 900, textTransform: 'capitalize' }}>
                            {calculatedPayStatus}
                          </div>
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>Notes</label><input style={inp} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any notes..." /></div>
                      </div>
                    </>
                  )}

                  {hasServiceSale && !hasDeviceSale && (
                    <>
                      <h4 style={{ color: '#0b66ae', margin: '0 0 12px' }}>Service Sale Dealer Tag</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
                        <div>
                          <label style={lbl}>Sale Type</label>
                          <select style={inp} value={saleType} onChange={e => setSaleType(e.target.value)}>
                            <option value="direct">Direct</option><option value="dealer">Dealer</option>
                            <option value="employee">Employee</option><option value="admin">Admin</option>
                          </select>
                        </div>
                        <div>
                          <label style={lbl}>Dealer / Sales User Tag</label>
                          <select style={inp} value={salesUserId} onChange={e => setSalesUserId(e.target.value)}>
                            <option value="">Current logged-in user</option>
                            {salesUsers.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                          </select>
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>Notes</label><input style={inp} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any notes..." /></div>
                      </div>
                    </>
                  )}

                  {hasServiceSale && (
                    <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '14px', marginTop: '16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                        <h4 style={{ color: '#0b66ae', margin: 0 }}>{hasDeviceSale ? 'Combo Service Add-ons' : 'Service Sale Details'}</h4>
                        <button type="button" onClick={() => addServiceAddon()} disabled={serviceTypes.length === 0} style={{ background: '#1e3a5f', color: 'white', border: 'none', borderRadius: '8px', padding: '8px 11px', cursor: 'pointer', fontSize: '12px', fontWeight: 800 }}>
                          + Add Service
                        </button>
                      </div>
                      {serviceTypes.length === 0 ? (
                        <small style={{ color: '#dc2626' }}>No service permission assigned yet. Services must be assigned by the Super Admin.</small>
                      ) : (
                        <div style={{ display: 'grid', gap: '10px' }}>
                          {quickServiceAddons.length > 0 && (
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              {quickServiceAddons.map((type) => (
                                <button key={type.id} type="button" onClick={() => addServiceAddon(type.id)} style={{ background: '#f8fafc', color: '#1e3a5f', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '8px 10px', cursor: 'pointer', fontSize: '12px', fontWeight: 800 }}>
                                  + {quickServiceLabel(type)}
                                </button>
                              ))}
                            </div>
                          )}
                          {serviceAddons.length === 0 ? (
                            <small style={{ color: '#64748b' }}>GPS renewal, insurance, fitness, PUC, permit, tax, FASTag, and other services can be selected with the sale.</small>
                          ) : (
                            serviceAddons.map((addon, index) => (
                              <div key={`${addon.service_type_id}-${index}`} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '8px', alignItems: 'end' }}>
                                <div>
                                  <label style={lbl}>Service</label>
                                  <select style={inp} value={addon.service_type_id} onChange={(event) => updateServiceAddon(index, 'service_type_id', event.target.value)}>
                                    {serviceTypes.map((type) => <option key={type.id} value={type.id}>{type.name}</option>)}
                                  </select>
                                </div>
                                <div>
                                  <label style={lbl}>Expiry</label>
                                  <input type="date" style={inp} value={addon.expiry_date} onChange={(event) => updateServiceAddon(index, 'expiry_date', event.target.value)} />
                                </div>
                                <div>
                                  <label style={lbl}>Amount</label>
                                  <input type="number" min="0" style={inp} value={addon.amount} onChange={(event) => updateServiceAddon(index, 'amount', event.target.value)} placeholder="0" />
                                </div>
                                <div>
                                  <label style={lbl}>Paid</label>
                                  <input type="number" min="0" style={inp} value={addon.paid_amount} onChange={(event) => updateServiceAddon(index, 'paid_amount', event.target.value)} placeholder="0" />
                                </div>
                                <div>
                                  <label style={lbl}>Notes</label>
                                  <input style={inp} value={addon.notes} onChange={(event) => updateServiceAddon(index, 'notes', event.target.value)} placeholder="Policy no, details..." />
                                </div>
                                <button type="button" onClick={() => removeServiceAddon(index)} style={{ background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: '8px', padding: '10px', cursor: 'pointer', fontWeight: 900 }}>
                                  X
                                </button>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <button type="submit" disabled={loading} style={{ marginTop: '16px', background: '#10b981', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '8px', fontSize: '15px', cursor: 'pointer', fontWeight: 'bold' }}>
                    {loading ? 'Saving...' : saleMode === 'services_only' ? 'Save Services Sale' : 'Save Sale'}
                  </button>
                </>
              )}
            </form>
          </div>
        )}

        {editingSale && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)', zIndex: 1000, padding: '22px', overflowY: 'auto' }}>
            <div style={{ maxWidth: '980px', margin: '0 auto', background: 'white', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '16px 20px', background: '#1e3a5f', color: 'white', alignItems: 'center', flexWrap: 'wrap' }}>
                <div>
                  <strong style={{ display: 'block', fontSize: '18px' }}>Edit Sale {editingSale.invoice_no || `#${editingSale.id}`}</strong>
                  <span style={{ fontSize: '13px', opacity: 0.85 }}>{editingSale.customer_name || 'Customer'} | {editingSale.vehicle_no || 'Vehicle'}</span>
                </div>
                <button type="button" onClick={() => { setEditingSale(null); setEditForm({}); }} style={{ background: 'white', color: '#1e3a5f', border: 'none', borderRadius: '8px', padding: '9px 13px', cursor: 'pointer', fontWeight: 800 }}>
                  Close
                </button>
              </div>
              <form onSubmit={saveEditedSale} style={{ padding: '20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '14px' }}>
                  <div>
                    <label style={lbl}>Customer Name</label>
                    <input style={inp} value={editForm.customer_name || ''} onChange={(event) => updateEditField('customer_name', event.target.value)} />
                  </div>
                  <div>
                    <label style={lbl}>Mobile</label>
                    <input style={inp} value={editForm.customer_mobile || ''} onChange={(event) => updateEditField('customer_mobile', event.target.value)} />
                  </div>
                  <div>
                    <label style={lbl}>Vehicle No</label>
                    <input style={inp} value={editForm.vehicle_no || ''} onChange={(event) => updateEditField('vehicle_no', event.target.value)} />
                  </div>
                  <div>
                    <label style={lbl}>Driver Name</label>
                    <input style={inp} value={editForm.driver_name || ''} onChange={(event) => updateEditField('driver_name', event.target.value)} />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={lbl}>Address</label>
                    <input style={inp} value={editForm.address || ''} onChange={(event) => updateEditField('address', event.target.value)} />
                  </div>
                  <div>
                    <label style={lbl}>Device Type</label>
                    <input style={inp} value={editForm.device_type || ''} onChange={(event) => updateEditField('device_type', event.target.value)} />
                  </div>
                  <div>
                    <label style={lbl}>Device Model</label>
                    <input style={inp} value={editForm.device_model || ''} onChange={(event) => updateEditField('device_model', event.target.value)} />
                  </div>
                  <div>
                    <label style={lbl}>Product</label>
                    <select style={inp} value={editForm.product_id || ''} onChange={(event) => updateEditField('product_id', event.target.value)}>
                      <option value="">Select product</option>
                      {products.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={lbl}>IMEI</label>
                    <input style={inp} value={editForm.imei || ''} onChange={(event) => updateEditField('imei', event.target.value)} />
                  </div>
                  <div>
                    <label style={lbl}>SIM No</label>
                    <input style={inp} value={editForm.sim_no || ''} onChange={(event) => updateEditField('sim_no', event.target.value)} />
                  </div>
                  <div>
                    <label style={lbl}>Quantity</label>
                    <input type="number" min="1" style={inp} value={editForm.quantity || 1} onChange={(event) => updateEditField('quantity', event.target.value)} />
                  </div>
                  <div>
                    <label style={lbl}>Sale Price</label>
                    <input type="number" min="0" style={inp} value={editForm.sale_price || ''} onChange={(event) => updateEditField('sale_price', event.target.value)} />
                  </div>
                  <div>
                    <label style={lbl}>Installation Charge</label>
                    <input type="number" min="0" style={inp} value={editForm.installation_charge || ''} onChange={(event) => updateEditField('installation_charge', event.target.value)} />
                  </div>
                  <div>
                    <label style={lbl}>Paid Amount</label>
                    <input type="number" min="0" style={inp} value={editForm.paid_amount || ''} onChange={(event) => updateEditField('paid_amount', event.target.value)} />
                  </div>
                  <div>
                    <label style={lbl}>Payment Mode</label>
                    <select style={inp} value={editForm.payment_mode || 'cash'} onChange={(event) => updateEditField('payment_mode', event.target.value)}>
                      {paymentOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={lbl}>Sale Type</label>
                    <select style={inp} value={editForm.sale_type || 'direct'} onChange={(event) => updateEditField('sale_type', event.target.value)}>
                      <option value="direct">Direct</option>
                      <option value="dealer">Dealer</option>
                      <option value="employee">Employee</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div>
                    <label style={lbl}>Dealer / Sales User Tag</label>
                    <select style={inp} value={editForm.sales_user_id || ''} onChange={(event) => updateEditField('sales_user_id', event.target.value)}>
                      <option value="">Current sale owner</option>
                      {salesUsers.map((item) => <option key={item.id} value={item.id}>{item.name} ({item.role})</option>)}
                    </select>
                  </div>
                  <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '8px', margin: '28px 0 0' }}>
                    <input type="checkbox" checked={Boolean(editForm.gps_tracking_enabled)} onChange={(event) => updateEditField('gps_tracking_enabled', event.target.checked)} />
                    GPS Tracking Enabled
                  </label>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={lbl}>Notes</label>
                    <input style={inp} value={editForm.notes || ''} onChange={(event) => updateEditField('notes', event.target.value)} />
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginTop: '18px', flexWrap: 'wrap' }}>
                  <div style={{ color: '#334155', fontSize: '13px', fontWeight: 800 }}>
                    Total: {money(editTotal)} | Paid: {money(editPaidAmount)} | Balance: {money(editBalance)}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button type="button" onClick={() => { setEditingSale(null); setEditForm({}); }} style={{ background: '#f1f5f9', color: '#334155', border: 'none', borderRadius: '8px', padding: '10px 15px', cursor: 'pointer', fontWeight: 800 }}>
                      Cancel
                    </button>
                    <button type="submit" disabled={loading} style={{ background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 15px', cursor: loading ? 'wait' : 'pointer', fontWeight: 800 }}>
                      {loading ? 'Saving...' : 'Save Update'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        {invoiceData && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)', zIndex: 1000, padding: '22px', overflowY: 'auto' }}>
            <div style={{ maxWidth: '920px', margin: '0 auto', background: 'white', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '16px 20px', background: '#1e3a5f', color: 'white', alignItems: 'center', flexWrap: 'wrap' }}>
                <div>
                  <strong style={{ display: 'block', fontSize: '18px' }}>Invoice {invoiceData.invoice_no}</strong>
                  <span style={{ fontSize: '13px', opacity: 0.85 }}>{invoiceData.customer?.name || 'Customer'} | {invoiceData.customer?.vehicle_no || 'Vehicle'}</span>
                  <span style={{ display: 'block', marginTop: '4px', fontSize: '12px', color: invoiceDraft?.html ? '#bbf7d0' : '#dbeafe' }}>
                    {invoiceDraft?.html ? `Edited draft saved - ${invoiceDraft.changes?.length || 0} change(s)` : 'Original invoice - database data'}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {invoiceDraft?.html && (
                    <button type="button" onClick={() => setInvoiceViewMode('edited')} style={{ background: invoiceViewMode === 'edited' ? '#10b981' : '#d1fae5', color: invoiceViewMode === 'edited' ? 'white' : '#065f46', border: 'none', borderRadius: '8px', padding: '9px 13px', cursor: 'pointer', fontWeight: 800 }}>View Edited</button>
                  )}
                  <button type="button" onClick={() => setInvoiceViewMode('original')} style={{ background: invoiceViewMode === 'original' ? '#38bdf8' : '#e0f2fe', color: invoiceViewMode === 'original' ? 'white' : '#075985', border: 'none', borderRadius: '8px', padding: '9px 13px', cursor: 'pointer', fontWeight: 800 }}>View Original</button>
                  <button type="button" onClick={() => saveEditedInvoiceDraft()} style={{ background: '#f59e0b', color: 'white', border: 'none', borderRadius: '8px', padding: '9px 13px', cursor: 'pointer', fontWeight: 800 }}>Save Edited Draft</button>
                  <button type="button" onClick={printEditedInvoice} style={{ background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', padding: '9px 13px', cursor: 'pointer', fontWeight: 800 }}>Print Edited</button>
                  <button type="button" onClick={printOriginalInvoice} style={{ background: '#eef2ff', color: '#3730a3', border: 'none', borderRadius: '8px', padding: '9px 13px', cursor: 'pointer', fontWeight: 800 }}>Print Original</button>
                  {invoiceDraft?.html && (
                    <button type="button" onClick={resetEditedInvoiceDraft} style={{ background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: '8px', padding: '9px 13px', cursor: 'pointer', fontWeight: 800 }}>Reset Edited</button>
                  )}
                  <button type="button" onClick={closeInvoice} style={{ background: 'white', color: '#1e3a5f', border: 'none', borderRadius: '8px', padding: '9px 13px', cursor: 'pointer', fontWeight: 800 }}>Close</button>
                </div>
              </div>
              <div style={{ padding: '10px 16px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb', color: '#334155', fontSize: '12px' }}>
                <strong>{invoiceViewMode === 'edited' ? 'Editable draft mode' : 'Original mode'}:</strong> The edited draft is only for invoice printing; sale amount, customer, and payment records are not updated.
                {invoiceDraft?.changes?.length > 0 && (
                  <div style={{ marginTop: '8px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '6px' }}>
                    {invoiceDraft.changes.slice(0, 8).map((change) => (
                      <div key={change.field} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '7px' }}>
                        <b>{change.label}</b><br />
                        <span style={{ color: '#64748b' }}>Original: {change.original || '-'}</span><br />
                        <span style={{ color: '#0f766e' }}>Edited: {change.edited || '-'}</span>
                      </div>
                    ))}
                    {invoiceDraft.changes.length > 8 && (
                      <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '7px', color: '#64748b' }}>
                        +{invoiceDraft.changes.length - 8} more change(s)
                      </div>
                    )}
                  </div>
                )}
              </div>
              <iframe
                ref={invoiceFrameRef}
                title="Invoice Preview"
                srcDoc={invoiceViewMode === 'edited' && invoiceDraft?.html ? invoiceDraft.html : buildInvoiceHtml(invoiceData)}
                style={{ width: '100%', height: '78vh', border: 0, display: 'block', background: '#f3f4f6' }}
              />
            </div>
          </div>
        )}

        {showSalesList && (
          <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: canManageSales ? '1840px' : '1680px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#1e3a5f', color: 'white' }}>
                  {['#', 'Invoice No', 'Customer', 'Mobile', 'Vehicle', 'Product', 'IMEI', 'SIM No', 'Tracking', 'Total', 'Paid', 'Balance', 'Installation', 'Type', 'Sales User', 'Payment', 'Date', 'Invoice', ...(canManageSales ? ['Actions'] : [])].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sales.length === 0 ? (
                  <tr><td colSpan={canManageSales ? 19 : 18} style={{ padding: '24px', textAlign: 'center', color: '#666' }}>No sales found</td></tr>
                ) : sales.map((s, i) => (
                  <tr key={s.id} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? 'white' : '#f9fafb' }}>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>{i + 1}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 'bold', color: '#1e3a5f' }}>
                      {s.invoice_no || '-'}
                      {hasSavedInvoiceDraft(s) && (
                        <span style={{ display: 'inline-block', marginLeft: '6px', background: '#fef3c7', color: '#92400e', padding: '2px 6px', borderRadius: '999px', fontSize: '11px' }}>
                          Edited
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 'bold' }}>{s.customer_name || '-'}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>{s.customer_mobile || '-'}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>{s.vehicle_no || '-'}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>{s.product_name || '-'}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>{s.imei || '-'}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>{s.sim_no || s.serial_no || '-'}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>
                      <span style={{ padding: '3px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold', background: Number(s.gps_tracking_enabled || 0) === 1 ? '#dcfce7' : '#f1f5f9', color: Number(s.gps_tracking_enabled || 0) === 1 ? '#166534' : '#475569' }}>
                        {Number(s.gps_tracking_enabled || 0) === 1 ? 'On' : 'Off'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 'bold', color: '#10b981' }}>{money(s.total_amount)}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 'bold', color: '#2563eb' }}>{money(s.paid_amount)}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 'bold', color: Number(s.balance_amount || 0) > 0 ? '#dc2626' : '#10b981' }}>{money(s.balance_amount)}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', color: '#f97316', fontWeight: 'bold' }}>{money(s.installation_charge)}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>
                      <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '3px 8px', borderRadius: '12px', fontSize: '12px' }}>{s.sale_type || '-'}</span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>
                      <span style={{ background: '#dcfce7', color: '#166534', padding: '3px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold' }}>
                        {s.tagged_user_name || s.sold_by_name || '-'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold', background: s.payment_status === 'paid' ? '#d1fae5' : s.payment_status === 'pending' ? '#fee2e2' : '#fef3c7', color: s.payment_status === 'paid' ? '#065f46' : s.payment_status === 'pending' ? '#dc2626' : '#92400e' }}>{s.payment_status || '-'}</span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>{s.created_at ? formatDate(s.created_at) : '-'}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <button
                        type="button"
                        onClick={() => openInvoice(s)}
                        disabled={invoiceLoadingId === s.id}
                        style={{ background: '#1e3a5f', color: 'white', border: 'none', borderRadius: '7px', padding: '7px 10px', cursor: invoiceLoadingId === s.id ? 'wait' : 'pointer', fontSize: '12px', fontWeight: 800 }}
                      >
                        {invoiceLoadingId === s.id ? 'Loading...' : 'Generate'}
                      </button>
                    </td>
                    {canManageSales && (
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            onClick={() => openEditSale(s)}
                            style={{ background: '#f59e0b', color: 'white', border: 'none', borderRadius: '7px', padding: '7px 10px', cursor: 'pointer', fontSize: '12px', fontWeight: 800 }}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteSaleRecord(s)}
                            style={{ background: '#dc2626', color: 'white', border: 'none', borderRadius: '7px', padding: '7px 10px', cursor: 'pointer', fontSize: '12px', fontWeight: 800 }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
